import { Menu } from "lucide-react";
import { useCallback, useState } from "react";
import { Link, NavLink } from "react-router";

import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import { Button } from "@punto/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@punto/ui/components/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@punto/ui/components/sheet";
import { Skeleton } from "@punto/ui/components/skeleton";

import { Can } from "@/modules/abilities";
import { OrganizationSelector } from "@/modules/auth/components/organization-selector";
import { UserAvatar } from "@/modules/auth/components/user-avatar";
import { useUserOrganizations } from "@/modules/auth/hooks/use-user-organizations";
import { routePaths } from "@/router/route-paths";

import { useNav } from "./use-nav";

const MenuItem = ({
  href,
  target,
  onClick,
  children,
}: {
  href: string;
  target?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) => {
  return (
    <NavLink
      to={href}
      target={target}
      onClick={onClick}
      className="text-muted-foreground transition-colors hover:text-foreground [&.active]:text-foreground"
    >
      {children}
    </NavLink>
  );
};

/**
 * `onNavigate` closes the burger sheet. Every link in here has to call it:
 * react-router navigates in place, so nothing unmounts the sheet on its own
 * and the menu would stay open over the section it just opened (P-2683).
 */
const MobileNav = ({ onNavigate }: { onNavigate: () => void }) => {
  const { t } = useTranslation();
  const { menuItems, homeLink } = useNav();

  return (
    <div className="flex flex-col gap-6">
      <SheetTitle className="sr-only">{t("Header.menu_open")}</SheetTitle>

      {/* The logo is hoisted out of `nav` so the organization switcher can sit
          between it and the sections without a `Select` landing inside the
          navigation landmark — it is a scope control, not a destination. */}
      <Link
        to={homeLink.href}
        onClick={onNavigate}
        aria-label={t("Header.home")}
        className="flex items-center gap-2 text-lg font-semibold"
      >
        <span>{homeLink.label}</span>
      </Link>

      {/* The organization switcher lives here rather than in the header bar:
          at 451px its 200px minimum pushed the bar into a horizontal scroll
          of the whole page (P-2683). It renders nothing for single-org users. */}
      <OrganizationSelector />

      <nav className="grid gap-6 text-lg font-medium">
        {menuItems.map((item) => (
          <MenuItem key={item.href} href={item.href} onClick={onNavigate}>
            {item.label}
          </MenuItem>
        ))}
      </nav>
    </div>
  );
};

const DesktopNav = () => {
  const { t } = useTranslation();
  const { menuItems, homeLink } = useNav();

  return (
    <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
      <Link
        to={homeLink.href}
        aria-label={t("Header.home")}
        className="flex flex-nowrap items-center gap-2 text-lg font-semibold md:text-base"
      >
        <span className="whitespace-nowrap">{homeLink.label}</span>
      </Link>
      {menuItems.map((item) => (
        <MenuItem key={item.href} href={item.href} target={item.target}>
          {item.label}
        </MenuItem>
      ))}
    </nav>
  );
};

const NewProcedureButton = () => {
  const { t } = useTranslation();

  return (
    <Can I="create" a="Procedure">
      <Button asChild>
        <Link to={routePaths.newProcedure}>{t("Header.new_procedure")}</Link>
      </Button>
    </Can>
  );
};

const HeaderActions = () => {
  const { isLoading } = useUserOrganizations();

  if (isLoading) return <Skeleton className="h-9 w-32 rounded-md" />;

  return (
    <>
      <NewProcedureButton />
      {/* Below `md` this same selector is rendered inside the burger sheet. */}
      <div className="hidden md:block">
        <OrganizationSelector />
      </div>
    </>
  );
};

const UserMenu = () => {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          className="h-8 w-8 rounded-full"
        >
          <div>
            <UserAvatar />
          </div>
          <span className="sr-only">{t("Header.user_menu")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild className="text-destructive">
          <Link to={routePaths.logout}>{t("Header.logout")}</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Header = () => {
  const { t } = useTranslation();
  // Controlled rather than left to Radix: closing on navigation is a decision
  // only this side knows how to make (see `MobileNav`).
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  const MobileSheet = (
    <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="shrink-0 md:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">{t("Header.menu_open")}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left">
        <MobileNav onNavigate={closeMenu} />
      </SheetContent>
    </Sheet>
  );

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-background">
      <div className="container flex items-center gap-5 px-4 md:px-6">
        <DesktopNav />
        {MobileSheet}
        <div className="flex w-full items-center gap-4 md:ml-auto md:gap-2 lg:gap-4">
          <div className="ml-auto flex flex-1 justify-end gap-2">
            <HeaderActions />
          </div>
          <UserMenu />
        </div>
      </div>
    </header>
  );
};

export { Header };
